import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, take, tap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { sub } from 'date-fns';
import { ActivationState } from '../../activation-store/store/reducers/activation.reducer';
import * as fromAppSettingsReducer from '../../app-settings/store/reducers/app-settings-config.reducer';
import * as fromAppSettingsStoreSelectors from '../../app-settings/store/selectors/app-settings.selectors';
import * as fromActivationReducer from '../../activation-store/store/reducers/activation.reducer';
import { TariffOptionModel } from '../../activation-store/store/models/tariff-data.model';


/**
 * Instances of this class are meant to communicate to the app presentation layer how to show payment methods picking options.
 */
export class PaymentMethodsUIConfiguration {
  isFound?: boolean;
  paymentMethodFormControlValue?: string;  // 'Bankkonto' | 'Guthaben' | 'PayPal'
  selectedFormControlValue?: boolean;
  shouldDisableButton?: boolean;
  shouldSetIncompatiblePaymentMethod?: boolean;
}

/**
 * Possible payment method identifiers as received via `app-settings` service from `system-config.json`.
 */
export enum PaymentMethodsEnum {
  SEPA = 'SEPA', // 'Bankkonto'
  PAYPAL = 'PAYPAL', // PayPal
  CREDIT = 'CREDIT', // 'Guthaben'
}

export const optionPropertyAllowedPaymentMethods = 'allowedPaymentMethods';
export const optionPropertyUseCreditAsFallbackPaymentMethod = 'useCreditAsFallbackPaymentMethod';

export const paymentMethodEnumToNameMap = {
  [PaymentMethodsEnum.SEPA]: 'Bankkonto',
  [PaymentMethodsEnum.PAYPAL]: 'PayPal',
  [PaymentMethodsEnum.CREDIT]: 'Guthaben',
};

/**
 * Set of utility methods to support picking payment methods.
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentMethodsService {

  /**
   * All enabled payment methods, sorted by `sort` value in increasing order.
   */
  private enabledPaymentMethods: {
    name?: string;
    enabled?: boolean;
    sort?: number;
    minAge18?: boolean;
  }[];

  /**
   * Extracted `CREDIT` payment method.
   */
  private creditPaymentMethod: {
    name?: string;
    enabled?: boolean;
    sort?: number;
    minAge18?: boolean;
  };


  constructor(
    private storeActivation: Store<ActivationState>,
    private appSettingsStore: Store<fromAppSettingsReducer.AppSettingsConfigState>,
  ) { }


  /**
   * Calculate payment methods UI parameters.
   * 
   * @param option Tariff option for which the payment method should be picked.
   * @param choosePaymentMethod Should the payment method picker be shown to end-user at all?
   * @param noSepaAllowed Equivalent to situations in which payments via bank are disabled (typically after failed customer credit check),
   *                      or not available for whatever reason.
   */
  public calculatePaymentMethodsParameters(
    option: TariffOptionModel,
    choosePaymentMethod: boolean,
    noSepaAllowed: boolean,
  ): Observable<PaymentMethodsUIConfiguration> {

    const paymentNotFound = of({
      shouldDisableButton: true,
    });

    if (!option) return paymentNotFound;

    if (choosePaymentMethod) {

      this.fetchEnabledPaymentMethods(noSepaAllowed);

      if (!this.enabledPaymentMethods?.length) return paymentNotFound;

      const allowedPaymentMethods =
        this.fetchOptionPropertyValue(option, optionPropertyAllowedPaymentMethods)
            ?.toUpperCase()
            .split(',')
            .map(item => item.trim());

      if (!allowedPaymentMethods?.length) return paymentNotFound;

      let retValue: PaymentMethodsUIConfiguration;
      this.enabledPaymentMethods.forEach(epm => { // traverse in sort order
        if (!retValue && this.isPaymentMethodEnabledAndAllowed(epm.name as PaymentMethodsEnum, allowedPaymentMethods)) {
          retValue = {
            isFound: true,
            paymentMethodFormControlValue: paymentMethodEnumToNameMap[epm.name],
            selectedFormControlValue: epm.name === PaymentMethodsEnum.SEPA || epm.name === PaymentMethodsEnum.PAYPAL,
            shouldDisableButton: false,
          };
        }
      });

      if (retValue) {
        return of(retValue);
      }

      // No payment method found so far, thus check whether failover to CREDIT is possible
      const useCreditAsFallbackPaymentMethod = !!this.fetchOptionPropertyValue(option, optionPropertyUseCreditAsFallbackPaymentMethod);
      if (useCreditAsFallbackPaymentMethod && this.creditPaymentMethod) {
        return of({
          isFound: true,
          paymentMethodFormControlValue: PaymentMethodsEnum.CREDIT,
          selectedFormControlValue: false, // TODO PAYPAL may be needed to adjust Boolean expression once PayPal payment method is introduced?
          shouldDisableButton: false,
        });
      }
  
      // At this point it became obvious it was not possible to identify _any_ payment method which is allowed and enabled at the same time
      return of({
        shouldSetIncompatiblePaymentMethod: true,
        shouldDisableButton: true,
      });

    } else { // Showing payment method picker not needed at all
  
      return of({
        shouldDisableButton: false,
      });
    }
  }

  /**
   * Generate a sorted array of names of payment methods to be offered for picking by the app user.
   * 
   * @param option Tariff option for which the payment method should be picked.
   * @param choosePaymentMethod Should the payment method picker be shown to end-user at all?
   * @param noSepaAllowed Equivalent to situations in which payments via bank are disabled (typically after failed customer credit check),
   *                      or not available for whatever reason.
   */
  public selectablePaymentMethods(
    option: TariffOptionModel,
    choosePaymentMethod: boolean,
    noSepaAllowed: boolean,
  ): Observable<string[]> {

    let paymentMethodNames: string[] = [];

    if (!option || !choosePaymentMethod) return of([]);

    this.fetchEnabledPaymentMethods(noSepaAllowed);
    // console.log(`in selectablePaymentMethods(): this.enabledPaymentMethods`, this.enabledPaymentMethods);

    if (!this.enabledPaymentMethods?.length) return of([]);

    let allowedPaymentMethods =
      this.fetchOptionPropertyValue(option, optionPropertyAllowedPaymentMethods)
          ?.toUpperCase()
          .split(',')
          .map(item => item.trim());
    // console.log(`in selectablePaymentMethods(): allowedPaymentMethods`, allowedPaymentMethods);

    if (!allowedPaymentMethods?.length) {

      // Check whether failover to CREDIT is possible?
      // TODO PAYPAL probably needs adaptation...
      const useCreditAsFallbackPaymentMethod = !!this.fetchOptionPropertyValue(option, optionPropertyUseCreditAsFallbackPaymentMethod);
      if (useCreditAsFallbackPaymentMethod && this.creditPaymentMethod) {
        return of([this.creditPaymentMethod.name]);
      } else {
        return of([]);
      }
    }

    this.enabledPaymentMethods.forEach(epm => { // traverse in sort order
      if (this.isPaymentMethodEnabledAndAllowed(epm.name as PaymentMethodsEnum, allowedPaymentMethods)) {
        paymentMethodNames.push(epm.name);
      }
    });

    // console.log(`in selectablePaymentMethods(): about to return: `, paymentMethodNames);
    return of(paymentMethodNames);
  }

  /**
   * Produces and returns an array of payment methods available to current customer based on:
   * 1. The set of enabled payment methods specified in `system-config.json`
   * 2. The minimum customer age threshold (if set for a particular payment method).
   * 3. Whether SEPA payments are allowed (after bad credit check, for example).
   * 
   * The array elements are sorted by the increasing sort order value.
   * 
   * @param noSepaAllowed Equivalent to situations in which payments via bank are disabled (typically after failed customer credit check),
   *                      or not available for whatever reason.
   */
  public getEnabledPaymentMethods(noSepaAllowed: boolean): Observable<{
    name?: string;
    enabled?: boolean;
    sort?: number;
    minAge18?: boolean;
  }[]  | undefined> {
    let enabledPaymentMethods;
    combineLatest([
      this.appSettingsStore.select(fromAppSettingsStoreSelectors.selectAppSettingsStateConfig),
      this.storeActivation.select(fromActivationReducer.selectActivationState)
    ]).pipe(
      take(1),
      map(stores => ({ appSettings: stores[0], activation: stores[1]})),
      tap(res => {
        const dateOfBirth = res?.activation?.customerData?.dateOfBirth ? new Date(res.activation.customerData.dateOfBirth) : undefined;
        enabledPaymentMethods = Object.entries(res.appSettings.paymentMethods)
                                      .filter(pm => Object.values(PaymentMethodsEnum).includes(pm[0] as PaymentMethodsEnum))
                                      .map(([name, value]) => ({ name, ...value }))
                                      .filter(pmNamed => pmNamed.enabled)
                                      .filter(pmNamed => pmNamed.minAge18 ? (dateOfBirth ? sub(new Date(), { years: 18 }) > dateOfBirth : false) : true)
                                      .filter(pmNamed => noSepaAllowed ? pmNamed.name !== PaymentMethodsEnum.SEPA : true)
                                      .sort((a, b) => a.sort - b.sort);
      }),
    ).subscribe();

    return of(enabledPaymentMethods);
  }

  /**
   * Converts enumeration payment method element to the payment method string recognizable in BE processing down the road.
   * 
   * @param pme Payment method enumeration element, like 'SEPA', 'PAYPAL', or 'CREDIT'.
   * @returns Payment method name like 'Bankkonto', 'PayPal', or 'Guthaben'.
   */
  public convertPaymentMethodEnumToName(pme: PaymentMethodsEnum): string {
    return paymentMethodEnumToNameMap[pme];
  }

  /**
   * Converts payment method string (recognizable in BE processing down the road) to counterpart enumeration payment method element.
   * 
   * @param pmn Payment method name like 'Bankkonto', 'PayPal', or 'Guthaben'.
   * @returns Payment method enumeration element, like 'SEPA', 'PAYPAL', or 'CREDIT'.
   */
  public convertPaymentMethodNameToEnum(pmn: string): string | undefined {
    switch (pmn) {
      case paymentMethodEnumToNameMap[PaymentMethodsEnum.SEPA]:
        return PaymentMethodsEnum.SEPA;
      case paymentMethodEnumToNameMap[PaymentMethodsEnum.PAYPAL]:
        return PaymentMethodsEnum.PAYPAL;
      case paymentMethodEnumToNameMap[PaymentMethodsEnum.CREDIT]:
        return PaymentMethodsEnum.CREDIT;
      default:
        return undefined;
    }
  }

  public fetchOptionPropertyAllowedPaymentMethods(option: TariffOptionModel) {
    return this.fetchOptionPropertyValue(option, optionPropertyAllowedPaymentMethods);
  }

  public fetchOptionPropertyUseCreditAsFallbackPaymentMethod(option: TariffOptionModel) {
    return this.fetchOptionPropertyValue(option, optionPropertyUseCreditAsFallbackPaymentMethod);
  }

  /**
   * Produces an array of payment methods available to current customer based on:
   * 1. The set of enabled payment methods specified in `system-config.json`
   * 2. The minimum customer age threshold (if set for a particular payment method).
   * 3. Whether SEPA payments are allowed (after bad credit check, for example).
   * 
   * The array elements are sorted by the increasing sort order value. They are stored in `enabledPaymentMethods` attribute of this object.
   * 
   * In addition to that, a check whether CREDIT payment method is available is made, and in case it is, the CREDIT payment method
   * information are stored in `creditPaymentMethod` attribute of this object.
   * 
   * @param noSepaAllowed Equivalent to situations in which payments via bank are disabled (typically after failed customer credit check),
   *                      or not available for whatever reason.
   */
  private fetchEnabledPaymentMethods(noSepaAllowed): void {
    combineLatest([
      this.appSettingsStore.select(fromAppSettingsStoreSelectors.selectAppSettingsStateConfig),
      this.storeActivation.select(fromActivationReducer.selectActivationState)
    ]).pipe(
      take(1),
      map(stores => ({ appSettings: stores[0], activation: stores[1]})),
      tap(res => {
        const dateOfBirth = res?.activation?.customerData?.dateOfBirth ? new Date(res.activation.customerData.dateOfBirth) : undefined;
        this.enabledPaymentMethods = Object.entries(res.appSettings.paymentMethods)
                                           .filter(pm => Object.values(PaymentMethodsEnum).includes(pm[0] as PaymentMethodsEnum))
                                           .map(([name, value]) => ({ name, ...value }))
                                           .filter(pmNamed => pmNamed.enabled)
                                           .filter(pmNamed => pmNamed.minAge18 ? (dateOfBirth ? sub(new Date(), { years: 18 }) > dateOfBirth : false) : true)
                                           .filter(pmNamed => noSepaAllowed ? pmNamed.name !== PaymentMethodsEnum.SEPA : true)
                                           .sort((a, b) => a.sort - b.sort);
        this.creditPaymentMethod = Object.entries(res.appSettings.paymentMethods)
                                         .filter(pm => pm[0] === PaymentMethodsEnum.CREDIT)
                                         .map(([name, value]) => ({ name, ...value }))
                                         .filter(pmNamed => pmNamed.enabled)
                                         .filter(pmNamed => pmNamed.minAge18 ? (dateOfBirth ? sub(new Date(), { years: 18 }) > dateOfBirth : false) : true)
                                         ?.[0];
      }),
    ).subscribe();
  }

  private fetchOptionPropertyValue(option: TariffOptionModel, propertyName: string): string | undefined {
    return option?.properties?.property?.filter(p => p.name === propertyName)[0]?.value;
  }

  private isPaymentMethodEnabledAndAllowed(paymentMethod: PaymentMethodsEnum, allowedPaymentMethods: string[]): boolean {
    if (!paymentMethod) return false;
    if (!allowedPaymentMethods?.length) return false;
    if (!this.enabledPaymentMethods?.length) return false;

    if (!allowedPaymentMethods.includes(paymentMethod)) return false;
    if (!this.enabledPaymentMethods.some(epm => epm.name === paymentMethod)) return false;

    return true;
  }
}
